import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { NETWORK_TOAST_DURATION_MS, NETWORK_TOAST_THROTTLE_MS } from '../utils/networkErrorConstants'
import { rumEvent } from '../utils/rum'
import { type ConnectivityStatus, useOptionalConnectivity } from './ConnectivityProvider'

export type NetworkToastSource = 'trpc' | 'urql' | 'fetch'

export type ShowNetworkToastArgs = {
  source: NetworkToastSource
  error?: unknown
  status?: ConnectivityStatus
}

export type NetworkToastApi = {
  showNetworkToast: (args: ShowNetworkToastArgs) => void
  clearNetworkToast: () => void
}

const NetworkToastContext = createContext<NetworkToastApi | null>(null)

const NetworkToastStyles = React.memo(() => (
  <style>{'@keyframes networkToastProgress { from { transform: scaleX(1); } to { transform: scaleX(0); } }'}</style>
))

NetworkToastStyles.displayName = 'NetworkToastStyles'

type ToastState = {
  isVisible: boolean
  shownAtMs: number
  source?: NetworkToastSource
  status?: ConnectivityStatus
}

export const NetworkToastProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const connectivity = useOptionalConnectivity()
  const lastAllowedAtRef = useRef<number>(0)
  const [toast, setToast] = useState<ToastState>({ isVisible: false, shownAtMs: 0 })

  const statusRef = useRef(connectivity?.status)
  useEffect(() => {
    statusRef.current = connectivity?.status
  }, [connectivity?.status])

  const clearNetworkToast = useCallback(() => {
    setToast(prev => ({ ...prev, isVisible: false }))
    rumEvent('network_toast_cleared', { status: statusRef.current })
  }, [])

  const showNetworkToast = useCallback(({ source, status }: ShowNetworkToastArgs) => {
    const now = Date.now()
    const lastAllowedAt = lastAllowedAtRef.current
    const throttledGate = now - lastAllowedAt < NETWORK_TOAST_THROTTLE_MS
    if (throttledGate) return

    lastAllowedAtRef.current = now

    setToast({ isVisible: true, shownAtMs: now, source, status: status ?? statusRef.current })
    rumEvent('network_toast_shown', { source, status: status ?? statusRef.current })
  }, [])

  useEffect(() => {
    if (connectivity?.status === 'healthy') {
      clearNetworkToast()
    }
  }, [clearNetworkToast, connectivity?.status])

  const value = useMemo<NetworkToastApi>(
    () => ({ showNetworkToast, clearNetworkToast }),
    [clearNetworkToast, showNetworkToast],
  )

  return (
    <NetworkToastContext.Provider value={value}>
      <NetworkToastStyles />
      {toast.isVisible ? (
        <div
          style={{
            position: 'fixed',
            top: 24,
            right: 24,
            zIndex: 9999,
            width: 360,
            maxWidth: 'calc(100vw - 48px)',
            pointerEvents: 'auto',
            transform: 'translateX(0)',
            transition: 'transform 120ms ease-out',
          }}
        >
          <div
            style={{
              background: '#fff',
              border: '1px solid rgba(0,0,0,0.08)',
              borderRadius: 8,
              padding: 12,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600 }}>Network issue</div>
                <div>Network issue detected. Some actions may not work.</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setToast(prev => ({ ...prev, isVisible: false }))
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 16,
                  lineHeight: 1,
                }}
                aria-label="Dismiss network toast"
              >
                ×
              </button>
            </div>
            <div
              style={{
                height: 4,
                background: 'rgba(0,0,0,0.06)',
                marginTop: 8,
                overflow: 'hidden',
                borderRadius: 999,
              }}
            >
              <div
                key={toast.shownAtMs}
                onAnimationEnd={() => {
                  setToast(prev => ({ ...prev, isVisible: false }))
                }}
                style={{
                  height: '100%',
                  width: '100%',
                  background: '#ff4d4f',
                  transformOrigin: 'left',
                  animation: `networkToastProgress ${NETWORK_TOAST_DURATION_MS}ms linear forwards`,
                }}
              />
            </div>
          </div>
        </div>
      ) : null}
      {children}
    </NetworkToastContext.Provider>
  )
}

export const useOptionalNetworkToast = () => useContext(NetworkToastContext)

export const useNetworkToast = () => {
  const context = useContext(NetworkToastContext)

  if (!context) {
    throw new Error('useNetworkToast must be used within a NetworkToastProvider')
  }

  return context
}
